import { useState, useEffect } from 'react';
import { Link2, Wand2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal, ModalFooter, Button, Input, PlatformIcon } from '@/components/ui';
import { detectPlatform, getPlatformConfig } from '@/utils/platformDetector';
import type { Platform } from '@/types';

interface CreateDeeplinkModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { originalUrl: string; name?: string }) => Promise<void>;
}

const CreateDeeplinkModal = ({ isOpen, onClose, onSubmit }: CreateDeeplinkModalProps) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [detectedPlatform, setDetectedPlatform] = useState<Platform | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Detect platform as user types
  useEffect(() => {
    if (url.length > 5) {
      const platform = detectPlatform(url);
      setDetectedPlatform(platform);
    } else {
      setDetectedPlatform(null);
    }
  }, [url]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!url.trim()) {
      setError(t('createModal.pleaseEnterUrl'));
      return;
    }

    // Basic URL validation
    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
    } catch {
      setError(t('createModal.pleaseEnterValidUrl'));
      return;
    }

    setIsLoading(true);

    try {
      const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;
      await onSubmit({
        originalUrl: normalizedUrl,
        name: name.trim() || undefined,
      });
      handleClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : String(err);

      // Parse DAILY_QUOTA_REACHED errors
      if (errorMessage.includes('DAILY_QUOTA_REACHED')) {
        const quota = errorMessage.split(':')[1];
        setError(t('createModal.dailyQuotaReached', { quota }));
      } else if (errorMessage.includes('URL_UNSAFE')) {
        const threatType = errorMessage.split(':')[1];
        switch (threatType) {
          case 'MALWARE':
            setError(t('createModal.urlMalware'));
            break;
          case 'SOCIAL_ENGINEERING':
            setError(t('createModal.urlPhishing'));
            break;
          case 'UNWANTED_SOFTWARE':
          case 'POTENTIALLY_HARMFUL_APPLICATION':
            setError(t('createModal.urlUnwanted'));
            break;
          default:
            setError(t('createModal.urlUnsafe'));
        }
      } else {
        setError(t('createModal.failedToCreate'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setUrl('');
    setName('');
    setDetectedPlatform(null);
    setError(null);
    onClose();
  };

  const platformConfig = detectedPlatform ? getPlatformConfig(detectedPlatform) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('createModal.title')}
      description={t('createModal.description')}
      size="md"
    >
      <form onSubmit={handleSubmit}>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <Input
              label={t('createModal.urlLabel')}
              type="text"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://open.spotify.com/track/..."
              leftIcon={<Link2 className="w-5 h-5" />}
              autoFocus
            />

            {/* Platform detection feedback */}
            {platformConfig && detectedPlatform !== 'generic' && (
              <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-lime-50 rounded-xl">
                <Wand2 className="w-4 h-4 text-lime-600" />
                <span className="text-sm text-lime-700">
                  {t('createModal.detected')} <strong>{platformConfig.name}</strong>
                </span>
                <div className="ml-auto">
                  <PlatformIcon platform={detectedPlatform!} size="sm" />
                </div>
              </div>
            )}
          </div>

          <Input
            label={t('createModal.nameLabel')}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('createModal.namePlaceholder')}
            hint={t('createModal.nameHint')}
          />
        </div>

        <ModalFooter>
          <Button variant="ghost" size="md" onClick={handleClose} disabled={isLoading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="md"
            isLoading={isLoading}
            leftIcon={!isLoading ? <Link2 className="w-4 h-4" /> : undefined}
          >
            {isLoading ? t('createModal.creating') : t('createModal.create')}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default CreateDeeplinkModal;
